// src/pages/CFOToolsPage.js
import React, { useEffect, useRef, useState, useMemo } from "react";
import { fetchCfoInsights } from "../utils/api";
import "./CFOToolsPage.css";
import Chart from "chart.js/auto";
import { useAppContext } from '../context/AppContext';
import ScenarioSliders from "../components/ScenarioSliders";

/** ---------- Definitions for tooltips (unchanged idea) ---------- */
const DEFINITIONS = {
  "cash runway": "Cash Runway = Current Cash ÷ Monthly Burn.",
  "burn rate": "Burn Rate = Average monthly net cash outflow.",
  "dso": "DSO = (Accounts Receivable ÷ Revenue) × Days — lower is better.",
  "dpo": "DPO = (Accounts Payable ÷ COGS) × Days — higher is better (within reason).",
  "dio": "DIO = (Inventory ÷ COGS) × Days — lower is better.",
  "ccc": "CCC = DSO + DIO − DPO — overall working capital cycle; lower is better."
};
function normalizeKey(s = "") {
  const t = String(s).trim().toLowerCase();
  if (DEFINITIONS[t]) return t;
  const compact = t.replace(/[^a-z0-9]/g, "");
  for (const key of Object.keys(DEFINITIONS)) {
    const k = key.toLowerCase();
    if (k === t) return key;
    if (k.replace(/[^a-z0-9]/g, "") === compact) return key;
  }
  return null;
}
function getDefinition(label, fallback) {
  const foundKey = normalizeKey(label);
  return foundKey ? DEFINITIONS[foundKey] : (fallback || null);
}
const InfoTip = ({ text }) => {
  if (!text) return null;
  return (
    <span className="info-tooltip" tabIndex={0} aria-label={`Definition: ${text}`}>
      <span className="info-badge" aria-hidden="true">i</span>
      <span className="tooltip-panel">{text}</span>
    </span>
  );
};
const Icon = {
  expand: (props) => (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" {...props}>
      <path d="M4 9V4h5M20 15v5h-5M20 9V4h-5M4 15v5h5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  line: (props) => (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" {...props}>
      <path d="M3 17l6-6 4 4 7-7" fill="none" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  bar: (props) => (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" {...props}>
      <path d="M6 20V10M12 20V6M18 20v-8" fill="none" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  close: (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" {...props}>
      <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
};

/** ---------- Phase 2 helpers already used ---------- */
function parseNumberLike(v) {
  if (v == null) return NaN;
  if (typeof v === 'number') return v;
  const s = String(v).replace(/[^0-9.\-∞]/g, '').replace(/,/g,'');
  if (s === '' || s.toLowerCase() === 'na' || s.toLowerCase() === 'n/a') return NaN;
  if (s.includes('∞') || s.toLowerCase().includes('inf')) return Infinity;
  const m = s.match(/-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : NaN;
}
function formatMoneyShort(n) {
  if (!isFinite(n)) return '∞';
  const sign = n < 0 ? '-' : '';
  const v = Math.abs(n);
  if (v >= 1e9) return `${sign}$${(v/1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${sign}$${(v/1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${sign}$${(v/1e3).toFixed(1)}K`;
  return `${sign}$${v.toFixed(0)}`;
}
function classifyKpi(label, raw) {
  const val = parseNumberLike(raw);
  const lower = String(label || '').toLowerCase();
  if (lower.includes('liquidity score') || lower.includes('efficiency score')) {
    if (val >= 75) return { badge: 'Healthy', sev: 'good', pct: Math.min(100, Math.max(0, val)) };
    if (val >= 50) return { badge: 'Watch', sev: 'med', pct: Math.min(100, Math.max(0, val)) };
    return { badge: 'At Risk', sev: 'high', pct: Math.min(100, Math.max(0, val)) };
  }
  if (lower.includes('runway')) {
    if (!isFinite(val)) return { badge: 'Surplus', sev: 'good', pct: 100 };
    if (val >= 12) return { badge: 'Healthy', sev: 'good', pct: Math.min(100, (val/24)*100) };
    if (val >= 6)  return { badge: 'Watch', sev: 'med', pct: Math.min(100, (val/24)*100) };
    return { badge: 'Critical', sev: 'high', pct: Math.min(100, (val/24)*100) };
  }
  if (lower.includes('burn category')) {
    const s = String(raw).toLowerCase();
    if (s.includes('net generator')) return { badge: 'Surplus', sev: 'good', pct: 100 };
    if (s.includes('mild')) return { badge: 'Mild', sev: 'med', pct: 66 };
    if (s.includes('moderate')) return { badge: 'Moderate', sev: 'med', pct: 50 };
    return { badge: 'Critical', sev: 'high', pct: 25 };
  }
  if (lower.includes('net margin')) {
    if (val >= 0.15) return { badge: 'Healthy', sev: 'good', pct: Math.min(100, (val/0.3)*100) };
    if (val >= 0.05) return { badge: 'Watch', sev: 'med', pct: Math.min(100, (val/0.3)*100) };
    return { badge: 'At Risk', sev: 'high', pct: Math.min(100, (val/0.3)*100) };
  }
  if (lower.includes('ccc')) { if (val <= 40) return { badge: 'Healthy', sev: 'good', pct: 80 }; if (val <= 80) return { badge: 'Watch', sev: 'med', pct: 55 }; return { badge: 'At Risk', sev: 'high', pct: 30 }; }
  if (lower.includes('dso')) { if (val <= 35) return { badge: 'Healthy', sev: 'good', pct: 80 }; if (val <= 55) return { badge: 'Watch', sev: 'med', pct: 55 }; return { badge: 'At Risk', sev: 'high', pct: 30 }; }
  if (lower.includes('dpo')) { if (val >= 45) return { badge: 'Healthy', sev: 'good', pct: 80 }; if (val >= 30) return { badge: 'Watch', sev: 'med', pct: 55 }; return { badge: 'At Risk', sev: 'high', pct: 30 }; }
  if (lower.includes('dio')) { if (val <= 30) return { badge: 'Healthy', sev: 'good', pct: 80 }; if (val <= 60) return { badge: 'Watch', sev: 'med', pct: 55 }; return { badge: 'At Risk', sev: 'high', pct: 30 }; }
  return { badge: null, sev: null, pct: null };
}
const MicroBar = ({ pct, sev }) => {
  if (pct == null || isNaN(pct)) return null;
  const w = Math.max(2, Math.min(100, Math.round(pct)));
  return (
    <div className={`microbar ${sev || 'med'}`} aria-hidden="true">
      <span style={{ width: `${w}%` }} />
    </div>
  );
};
/** id helper */
function sidOf(section, idx) {
  return (section && (section.key || section.title))
    ? `s-${String(section.key || section.title).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`
    : `s-${idx}`;
}

/** ---------- Component ---------- */
const CFOToolsPage = () => {
  const { user, csvData, columnTypes, companyName } = useAppContext();
  const trialLocked = !!(user?.trial_active && !user?.is_admin);
  if (trialLocked) {
    return <div className="cfo-error">Feature locked during trial. Please upgrade to Core.</div>;
  }

  const [insights, setInsights] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // tabs & search removed
  const [chartMode, setChartMode] = useState({});
  const [modalSid, setModalSid] = useState(null);
  const chartRefs = useRef({});

  const destroyChart = (id) => {
    if (chartRefs.current[id]) { try { chartRefs.current[id].destroy(); } catch {} delete chartRefs.current[id]; }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const token = localStorage.getItem('clairifi_token') || localStorage.getItem('token') || undefined;
        const data = await fetchCfoInsights({
          token,
          rows: csvData,           // <- send your active table
          columnTypes,             // <- send the mapping (even partial)
          companyName,
          userId: user?.id,
        });
        if (!mounted) return;
        setInsights({
          sections: data?.sections || [],
          warnings: data?.warnings || [],
          valuation: data?.valuation || [],
          meta: data?.meta || {},
          usage: data?.usage || {},
        });
      } catch (err) {
        console.error("[CFOTools] fetch failed:", err);
        setError("Failed to load CFO Tools.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
      Object.keys(chartRefs.current).forEach(destroyChart);
    };
  }, []);

  /** Build chart config */
  const buildChartConfig = (section, sid, forModal = false) => {
    const chart = section.chart || {};
    const typeRaw = chart.type || "line";
    const isDonut = typeRaw === "donut";
    const type = isDonut ? "doughnut" : (chartMode[sid] || typeRaw);

    const datasets = (Array.isArray(chart.datasets) && chart.datasets.length
      ? chart.datasets
      : [{ label: section.title, data: chart.data || [] }]
    ).map((d) => ({ ...d, borderWidth: d.borderWidth ?? 2, tension: d.tension ?? 0.25 }));

    return {
      type,
      data: { labels: chart.labels || [], datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: isDonut },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const v = ctx.parsed;
                const lab = ctx.dataset?.label || ctx.label || "";
                const unit = chart.unit || "";
                return unit ? `${lab}: ${v}${unit}` : `${lab}: ${v}`;
              }
            },
          },
        },
        scales: isDonut ? {} : {
          y: { beginAtZero: true },
          x: { ticks: { autoSkip: true, maxTicksLimit: forModal ? 24 : 14 } }
        },
      },
    };
  };

  /** Render visible charts */
  const renderCharts = (sections) => {
    const visibleIds = new Set(Array.from(document.querySelectorAll("canvas[data-cfo-chart]"), el => el.id));
    Object.keys(chartRefs.current).forEach((id) => { if (!visibleIds.has(id)) destroyChart(id); });

    sections.forEach((section, idx) => {
      if (!section.chart) return;
      const sid = sidOf(section, idx);
      const canvasId = `cfo-chart-${sid}`;
      const el = document.getElementById(canvasId);
      if (!el) return;
      destroyChart(canvasId);
      chartRefs.current[canvasId] = new Chart(el.getContext("2d"), buildChartConfig(section, sid));
    });
  };

  useEffect(() => {
    if (!insights) return;
    renderCharts(insights.sections || []);
    // eslint-disable-next-line
  }, [insights, chartMode]);

  if (loading) {
    return (
      <div className="cfo-page">
        <div className="cfo-title">CFO Financial Insights</div>
        <div className="cfo-skeleton" />
        <div className="cfo-skeleton" />
      </div>
    );
  }
  if (error) return <div className="cfo-error">{error}</div>;
  if (!insights) return null;

  const LabeledWithTip = ({ label, fallback }) => {
    const def = useMemo(() => getDefinition(label, fallback), [label, fallback]);
    return (
      <span className="label-with-tip">
        <span>{label}</span>
        <InfoTip text={def} />
      </span>
    );
  };

  const openModal = (section, idx) => {
    const sid = sidOf(section, idx);
    setModalSid(sid);
    setTimeout(() => {
      const el = document.getElementById(`cfo-modal-chart-${sid}`);
      if (!el) return;
      chartRefs.current[`modal-${sid}`] = new Chart(el.getContext("2d"), buildChartConfig(section, sid, true));
    }, 0);
  };
  const closeModal = () => {
    if (modalSid) { try { destroyChart(`modal-${modalSid}`); } catch {} }
    setModalSid(null);
  };

  /** -------- Extract baseline burn/runway & cash for Scenario Sliders -------- */
  const burnSection = (insights.sections || []).find(s =>
    (s.key && s.key === 'burn_runway') ||
    (s.title && /burn\s*rate|runway/i.test(s.title))
  );
  let baselineMonthlyBurn = NaN;
  let baselineRunway = NaN;
  if (burnSection?.kpis) {
    const burnK = burnSection.kpis.find(k => /monthly burn/i.test(k.label));
    const rwK = burnSection.kpis.find(k => /runway/i.test(k.label) && !/rev|opex/i.test(k.label));
    baselineMonthlyBurn = parseNumberLike(burnK?.value);
    baselineRunway = parseNumberLike(rwK?.value);
  }

  // Try to get latest Cash from Cash Strength chart (dataset label 'Cash', unit 'B')
  const cashSection = (insights.sections || []).find(s =>
    (s.key && s.key === 'cash_strength') ||
    (s.title && /cash\s*flow/i.test(s.title))
  );
  let cashBalance = NaN;
  try {
    const ds = cashSection?.chart?.datasets || [];
    const cashDs = ds.find(d => /cash/i.test(d.label || ""));
    if (cashDs && Array.isArray(cashDs.data) && cashDs.data.length > 0) {
      const lastB = cashDs.data[cashDs.data.length - 1]; // billions
      cashBalance = typeof lastB === 'number' ? lastB * 1e9 : NaN;
    }
  } catch {}

  // Fallback: infer cash if runway is finite and burn is known
  if ((!isFinite(cashBalance) || isNaN(cashBalance)) && isFinite(baselineRunway) && isFinite(baselineMonthlyBurn)) {
    cashBalance = baselineRunway * baselineMonthlyBurn;
  }

  return (
    <div className="cfo-page" data-density="comfortable">
      {/* Header */}
      <div className="cfo-header">
        <div>
          <h2 className="cfo-title">CFO Financial Insights</h2>
          <p className="cfo-subtitle">
            {insights.meta?.granularity ? `Analysis by ${insights.meta.granularity}.` : "Smart, dynamic insights from your latest data."}
          </p>
        </div>

        {/* actions removed */}

      </div>



      {/* Top KPI band (first section’s first 5 KPIs) */}
      {insights.sections?.[0]?.kpis?.length > 0 && (
        <div className="cfo-kpi-row">
          {insights.sections[0].kpis.slice(0, 5).map((k, i) => {
            const cls = classifyKpi(k.label, k.value);
            return (
              <div className="cfo-kpi" key={`kpi-${i}`}>
                <div className="cfo-kpi-label"><LabeledWithTip label={k.label} /></div>
                <div className="cfo-kpi-value">{k.value}</div>
                {cls.badge && <div className={`status-badge ${cls.sev}`}>{cls.badge}</div>}
                <MicroBar pct={cls.pct} sev={cls.sev} />
              </div>
            );
          })}
        </div>
      )}

      {/* Content grid */}
      <div className="cfo-grid">
        {(insights.sections || []).map((section, idx) => {
          const sid = sidOf(section, idx);
          const chart = section.chart;
          const isDonut = chart?.type === "donut";
          const canToggleType = !!chart && !isDonut;
          const currentMode = chartMode[sid] || chart?.type || "line";

          const sectionCard = (
            <div className="cfo-card" key={sid} data-key={sid}>
              <div className="cfo-card-head">
                <div className="cfo-card-title">
                  <span className="label-with-tip">
                    <span>{section.title}</span>
                    <InfoTip text={getDefinition(section.title, section.description)} />
                  </span>
                </div>
                {section.description && (<div className="cfo-card-desc">{section.description}</div>)}

                <div className="cfo-card-actions">
                  {chart?.unit && <span className="unit-badge" title="Chart unit">{chart.unit}</span>}
                  {canToggleType && (
                    <button
                      className="icon-btn"
                      onClick={() => setChartMode((m) => ({ ...m, [sid]: m[sid] === "bar" ? "line" : "bar" }))}
                      title={`Switch to ${currentMode === "bar" ? "line" : "bar"} chart`}
                      aria-label="Toggle chart type"
                    >
                      {currentMode === "bar" ? <Icon.line /> : <Icon.bar />}
                    </button>
                  )}
                  {chart && (
                    <button
                      className="icon-btn"
                      onClick={() => openModal(section, idx)}
                      title="Expand chart"
                      aria-label="Expand chart"
                    >
                      <Icon.expand />
                    </button>
                  )}
                </div>
              </div>

              {/* KPIs */}
              {!!(section.kpis && section.kpis.length) && (
                <div className="cfo-kpi-grid">
                  {section.kpis.map((k, i) => {
                    const cls = classifyKpi(k.label, k.value);
                    return (
                      <div className="cfo-kpi mini" key={i}>
                        <div className="cfo-kpi-label"><LabeledWithTip label={k.label} /></div>
                        <div className="cfo-kpi-value">{k.value}</div>
                        {cls.badge && <div className={`status-badge ${cls.sev}`}>{cls.badge}</div>}
                        <MicroBar pct={cls.pct} sev={cls.sev} />
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Chart */}
              {chart && (
                <div className="cfo-chart">
                  <canvas id={`cfo-chart-${sid}`} data-cfo-chart />
                </div>
              )}
            </div>
          );

          // Inject Scenario Sliders right AFTER the Burn & Runway card
          const isBurnRunway = (section.key === 'burn_runway') ||
            (section.title && /burn\s*rate|runway/i.test(section.title));

          return (
            <React.Fragment key={`${sid}-frag`}>
              {sectionCard}
              {isBurnRunway && (
                <div className="cfo-card">
                  <div className="cfo-card-head">
                    <div className="cfo-card-title">Scenario Simulator (client-side)</div>
                    <div className="cfo-card-desc">
                      Adjust revenue and OpEx to see instant impact on burn and runway. No data is sent to the server.
                    </div>
                  </div>

                  <ScenarioSliders
                    baselineMonthlyBurn={baselineMonthlyBurn}   // dollars per month (parsed)
                    baselineRunwayMonths={baselineRunway}       // months (parsed)
                    cashBalance={cashBalance}                    // dollars
                    formatter={formatMoneyShort}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Warnings */}
      {insights.warnings?.length > 0 && (
        <div className="cfo-card">
          <div className="cfo-card-title">Risk Warnings</div>
          <ul className="cfo-warning-list">
            {insights.warnings.map((w, i) => {
              const t = String(w).toLowerCase();
              const sev = /under\s*9\s*months|critical|declining|volatile|faster/.test(t) ? "high" : (/under\s*12\s*months|slow|watch/.test(t) ? "med" : "low");
              return (
                <li key={i} className={`warning-chip ${sev}`}>{w}</li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Modal */}
      {modalSid && (
        <div className="cfo-modal" role="dialog" aria-modal="true" aria-label="Expanded chart">
          <div className="cfo-modal-backdrop" onClick={closeModal} />
          <div className="cfo-modal-content">
            <button className="modal-close" onClick={closeModal} aria-label="Close">
              <Icon.close />
            </button>
            <div className="cfo-modal-body">
              <canvas id={`cfo-modal-chart-${modalSid}`} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CFOToolsPage;
